import { useState } from 'react';
import { Button, Card, Modal } from '@playerbase/ui';
import type { FeedbackForm } from '@playerbase/types';

import { disableFeedbackForm } from '../../../games/feedback-forms-data.js';

interface DisableConfirmModalProps {
  open: boolean;
  form: FeedbackForm;
  onClose: () => void;
  onDisabled: (form: FeedbackForm) => void;
}

export function DisableConfirmModal({
  open,
  form,
  onClose,
  onDisabled,
}: DisableConfirmModalProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setBusy(true);
    setError(null);
    const result = await disableFeedbackForm(form.id);
    if (!result.ok) {
      setBusy(false);
      setError('Could not disable the form. Please try again.');
      return;
    }
    setBusy(false);
    setError(null);
    onDisabled(result.data!);
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) {
          setError(null);
          onClose();
        }
      }}
      labelledById={`disable-form-${form.id}`}
      busy={busy}
      size="sm"
    >
      <Card>
        <Card.Header>
          <Card.Title id={`disable-form-${form.id}`}>Disable form</Card.Title>
          <Card.Description>
            Take <strong>{form.name}</strong> down? The button-message in Discord will be removed and
            players won&rsquo;t be able to submit new responses. Existing responses are kept.
          </Card.Description>
        </Card.Header>
        {error !== null ? (
          <Card.Content>
            <p className="text-sm text-danger-text">{error}</p>
          </Card.Content>
        ) : null}
        <Card.Footer>
          <Button
            variant="ghost"
            onClick={() => {
              setError(null);
              onClose();
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleConfirm()}
            disabled={busy}
          >
            {busy ? 'Disabling...' : 'Disable form'}
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}
